import React, { useState } from "react";

// React < 18 fallback for useId
let __qp_uid = 0;
function useStableId(prefix = 'qp') {
  const ref = React.useRef && React.useRef(null);
  // If hooks not available or useRef missing, fall back to a simple incrementing id
  if (!ref) {
    __qp_uid += 1;
    return `${prefix}-${__qp_uid}`;
  }
  if (!ref.current) {
    __qp_uid += 1;
    ref.current = `${prefix}-${__qp_uid}`;
  }
  return ref.current;
}


/**
 * Drop-in quantity pickers for values 0..10 (configurable).
 * All components are controlled: pass value + onChange.
 * Minimal, dependency-free, accessible, and easy to style.
 */

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
const range = (start, end) => Array.from({ length: end - start + 1 }, (_, i) => start + i);

/** SLIDER **/
export function QuantitySlider({ value, onChange, min = 0, max = 10, step = 1, label = "Cantidad", id }) {
  const inputId = id || useStableId();
  return (
    <div className="qp-block">
      <label htmlFor={inputId} className="qp-label">{label}: <strong>{value}</strong></label>
      <input
        id={inputId}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange && onChange(Number(e.target.value))}
        className="qp-slider"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
      />
      <div className="qp-marks">
        {range(min, max).map((n) => (
          <span key={n} className="qp-mark" aria-hidden>{n}</span>
        ))}
      </div>
    </div>
  );
}

/** STEPPER (- / +) **/
export function QuantityStepper({ value, onChange, min = 0, max = 10, label = "Cantidad" }) {
  const dec = () => onChange && onChange(clamp(value - 1, min, max));
  const inc = () => onChange && onChange(clamp(value + 1, min, max));
  return (
    <div className="qp-block">
      <div className="qp-label">{label}</div>
      <div className="qp-stepper" role="group" aria-label={`${label} stepper`}>
        <button type="button" className="qp-btn" onClick={dec} disabled={value <= min} aria-label="decrease">-</button>
        <div className="qp-value" aria-live="polite" aria-atomic>{value}</div>
        <button type="button" className="qp-btn" onClick={inc} disabled={value >= max} aria-label="increase">+</button>
      </div>
    </div>
  );
}

export function QuantityRentalStepper({ value, onChange, min = 0, max = 10, label = "Cantidad" }) {
  const dec = () => onChange && onChange(clamp(value - 1, min, max));
  const inc = () => onChange && onChange(clamp(value + 1, min, max));
  return (
    <div className="qp-block">
      <div className="qp-label">{label}</div>
      <div className="rental-qp-stepper" role="group" aria-label={`${label} stepper`}>
        <button type="button" className="rental-qp-btn" onClick={dec} disabled={value <= min} aria-label="decrease">-</button>
        <div className="rental-qp-value" aria-live="polite" aria-atomic>{value}</div>
        <button type="button" className="rental-qp-btn" onClick={inc} disabled={value >= max} aria-label="increase">+</button>
      </div>
    </div>
  );
}

/** BUTTON GRID (1..10) **/
export function QuantityButtonGrid({ value, onChange, min = 0, max = 10, columns = 5, label = "Cantidad" }) {
  const nums = range(min, max);
  return (
    <div className="qp-block">
      <div className="qp-label">{label}</div>
      <div className="qp-grid" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }} role="radiogroup" aria-label={`${label} botones`}>
        {nums.map((n) => (
          <button
            key={n}
            type="button"
            className={`qp-chip ${n === value ? "qp-chip--active" : ""}`}
            aria-checked={n === value}
            role="radio"
            onClick={() => onChange && onChange(n)}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

/** CHIP ROW (1..10) **/
export function QuantityChipGroup({ value, onChange, min = 0, max = 10, label = "Cantidad" }) {
  const nums = range(min, max);
  return (
    <div className="qp-block">
      <div className="qp-label">{label}</div>
      <div className="qp-row" role="radiogroup" aria-label={`${label} chips`}>
        {nums.map((n) => (
          <button
            key={n}
            type="button"
            className={`qp-chip ${n === value ? "qp-chip--active" : ""}`}
            aria-checked={n === value}
            role="radio"
            onClick={() => onChange && onChange(n)}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

/** CHECKBOX BAR (progress-style) **/
export function QuantityCheckboxBar({ value, onChange, min = 0, max = 10, label = "Cantidad" }) {
  const nums = range(min, max);
  const handleToggle = (i, checked) => {
    if (checked) onChange && onChange(i);
    else onChange && onChange(Math.max(min, i - 1));
  };
  return (
    <div className="qp-block">
      <div className="qp-label">{label}: <strong>{value}</strong></div>
      <div className="qp-checkbar" aria-label={`${label} checkboxes`}>
        {nums.map((n, idx) => {
          const checked = n <= value;
          return (
            <label key={n} className={`qp-check ${checked ? "qp-check--on" : ""}`}>
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => handleToggle(n, e.target.checked)}
                aria-label={`${n}`}
              />
            </label>
          );
        })}
      </div>
    </div>
  );
}

/** SEGMENTED CONTROL **/
export function QuantitySegmented({ value, onChange, min = 0, max = 10, label = "Cantidad" }) {
  const nums = range(min, max);
  return (
    <div className="qp-block">
      <div className="qp-label">{label}</div>
      <div className="qp-segmented" role="radiogroup" aria-label={`${label} segmentado`}>
        {nums.map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            className={`qp-seg ${value === n ? "qp-seg--active" : ""}`}
            onClick={() => onChange && onChange(n)}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * DEMO GALLERY (default export)
 * Shows all pickers wired to the same state. Remove in production and use individual components.
 */
export default function QuantityPickerGallery() {
  const [qty, setQty] = useState(0);
  return (
    <div className="qp-wrap">
      <h2 style={{ marginBottom: 8 }}>Quantity Picker Alternatives</h2>
      <p style={{ marginTop: 0, opacity: .7 }}>All examples control the same value. Current: <strong>{qty}</strong></p>

      <QuantitySlider value={qty} onChange={setQty} />
      <QuantityStepper value={qty} onChange={setQty} />
      <QuantityRentalStepper value={qty} onChange={setQty} />
      <QuantityButtonGrid value={qty} onChange={setQty} />
      <QuantityChipGroup value={qty} onChange={setQty} />
      <QuantityCheckboxBar value={qty} onChange={setQty} />
      <QuantitySegmented value={qty} onChange={setQty} />

      <style>{`
        .qp-wrap { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, Arial, "Apple Color Emoji", "Segoe UI Emoji"; line-height: 1.35; padding: 16px; }
        .qp-block { margin: 16px 0 24px; }
        .qp-label { font-size: 14px; color: #374151; margin-bottom: 8px; }
        .qp-slider { width: 100%; }
        .qp-marks { display: grid; grid-template-columns: repeat(11, 1fr); gap: 4px; margin-top: 8px; font-size: 12px; color: #6b7280; }
        .qp-mark { text-align: center; }

        .qp-stepper { display: inline-flex; align-items: center; gap: 8px; border: 1px solid #e5e7eb; border-radius: 12px; padding: 6px; }
        .qp-btn { appearance: none; border: 0; background: #f3f4f6; padding: 8px 12px; border-radius: 10px; cursor: pointer; font-size: 16px; }
        .qp-btn:disabled { opacity: .4; cursor: not-allowed; }
        .qp-value { min-width: 2ch; text-align: center; font-variant-numeric: tabular-nums; font-weight: 600; }

        .qp-row { display: flex; flex-wrap: wrap; gap: 8px; }
        .qp-grid { display: grid; gap: 8px; }
        .qp-chip { padding: 8px 12px; border-radius: 999px; border: 1px solid #e5e7eb; background: white; cursor: pointer; }
        .qp-chip--active { background: #111827; color: white; border-color: #111827; }

        .qp-checkbar { display: grid; grid-template-columns: repeat(11, minmax(0,1fr)); gap: 6px; }
        .qp-check { height: 28px; border-radius: 6px; border: 1px solid #e5e7eb; background: #fff; display: inline-flex; align-items: center; justify-content: center; }
        .qp-check--on { background: #111827; }
        .qp-check input { width: 0; height: 0; opacity: 0; position: absolute; }

        .qp-segmented { display: flex; border: 1px solid #e5e7eb; border-radius: 999px; overflow: hidden; }
        .qp-seg { flex: 1; padding: 8px 10px; background: white; border: 0; cursor: pointer; }
        .qp-seg + .qp-seg { border-left: 1px solid #e5e7eb; }
        .qp-seg--active { background: #111827; color: white; }
      `}</style>
    </div>
  );
}
